import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";

export default function MobileProfileSheet({ open, onClose }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const fileRef = useRef(null);
  const [stats, setStats] = useState({ tasks: '—', briefs: '—', streak: '—' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/me/dashboard").then(r => {
        const d = r.data || {};
        const tasks = (d.my_tasks || []).filter(t => t.status === 'Done' || t.status === 'Completed').length;
        const briefs = (d.my_briefs || []).length;
        setStats({ tasks: tasks || '—', briefs: briefs || '—', streak: d.streak || '—' });
      }).catch(() => {});
    }
  }, [open]);

  if (!open) return null;

  const name = user?.full_name || user?.name || "User";
  const role = user?.role || "Team Member";
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const hue = name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  const pic = user?.profile_picture;

  const handlePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/me/profile-picture', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      window.location.reload(); // Refresh to show new pic
    } catch (err) { console.error('Upload failed:', err); }
    setUploading(false);
  };

  const isAdmin = user?.role?.toLowerCase() === 'admin' || user?.email === 'dhruvabrol@gmail.com';
  const menuItems = [
    { icon: '◈', label: 'Leads', sub: 'Sales pipeline', path: '/bizdev/leads' },
    { icon: '▦', label: 'Clients', sub: 'Active accounts', path: '/bizdev/accounts' },
    { icon: '▣', label: 'NC Projects', sub: 'Non-client initiatives', path: '/projects' },
    { icon: '✦', label: 'ARIA', sub: 'AI workspace', path: '/ai' },
    ...(isAdmin ? [
      { icon: '👥', label: 'Admin', sub: 'Team & settings', path: '/admin' },
      { icon: '🐛', label: 'Bug Reports', sub: 'View submitted bugs', path: '/admin/bugs' },
    ] : []),
    { icon: '⚙️', label: 'Settings' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 150 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.3)' }} onClick={onClose} />
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: '#FFF', borderRadius: '16px 16px 0 0',
        padding: '16px 16px 32px', maxHeight: '80vh', overflowY: 'auto',
        animation: 'sheetUp .25s ease-out',
      }}>
        {/* Handle */}
        <div style={{ width: 36, height: 4, background: '#C7C7C7', borderRadius: 2, margin: '0 auto 14px' }} />

        {/* IG Profile Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
          {/* Avatar with gradient ring + camera overlay */}
          <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => fileRef.current?.click()}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'conic-gradient(from 180deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {pic ? (
                  <img src={pic} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: `hsl(${hue}, 50%, 40%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#fff' }}>{initials}</div>
                )}
              </div>
            </div>
            {/* Camera icon overlay */}
            <div style={{ position: 'absolute', bottom: -2, right: -2, width: 22, height: 22, borderRadius: '50%', background: '#0095F6', border: '2px solid #FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff' }}>📷</div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePicUpload} />
          </div>
          {/* Stats */}
          <div style={{ display: 'flex', flex: 1, justifyContent: 'space-around', paddingTop: 8 }}>
            {[
              { v: stats.tasks, l: 'tasks' },
              { v: stats.briefs, l: 'briefs won' },
              { v: stats.streak, l: 'day streak' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#262626' }}>{s.v}</div>
                <div style={{ fontSize: 10, color: '#8E8E8E' }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bio */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#262626' }}>{name}</div>
          <div style={{ fontSize: 13, color: '#8E8E8E' }}>{role} · Dentsu AMP</div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          <button style={{ flex: 1, padding: 7, borderRadius: 8, background: '#EFEFEF', color: '#262626', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', textAlign: 'center' }}>Edit profile</button>
          <button style={{ flex: 1, padding: 7, borderRadius: 8, background: '#EFEFEF', color: '#262626', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', textAlign: 'center' }}>Share profile</button>
        </div>

        {/* Story highlights */}
        <div style={{ display: 'flex', gap: 14, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 12, borderBottom: '1px solid #EFEFEF', marginBottom: 8 }}>
          {[
            { icon: '👥', label: 'Team' },
            { icon: '▣', label: 'Projects' },
            { icon: '✦', label: 'ARIA' },
            { icon: '🎨', label: 'Theme' },
            { icon: '⚙️', label: 'Settings' },
          ].map((h, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer', flexShrink: 0 }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', border: '1px solid #C7C7C7', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FFF', fontSize: 16 }}>{h.icon}</div>
              <span style={{ fontSize: 9, color: '#262626', maxWidth: 52, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.label}</span>
            </div>
          ))}
        </div>

        {/* Menu items */}
        {menuItems.map((item, i) => (
          <div key={i} onClick={() => { if (item.path) { navigate(item.path); onClose(); }}}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 0', borderBottom: '1px solid #EFEFEF', cursor: 'pointer' }}>
            <div style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{item.icon}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#262626' }}>{item.label}</div>
              {item.sub && <div style={{ fontSize: 10, color: '#8E8E8E' }}>{item.sub}</div>}
            </div>
            <span style={{ marginLeft: 'auto', color: '#C7C7C7', fontSize: 14 }}>›</span>
          </div>
        ))}

        {/* Sign out */}
        <div onClick={() => { logout(); onClose(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 0', cursor: 'pointer' }}>
          <div style={{ fontSize: 16, width: 24, textAlign: 'center' }}>🚪</div>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#DC2626' }}>Sign Out</div>
        </div>
      </div>
      <style>{`@keyframes sheetUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
    </div>
  );
}
